<template>
    <div style="width:100%; height:100vh; display:flex; justify-content:center; align-items:center">
        <a-spin v-if="loading" :indicator="indicator" tip="Aguarde, carregando CNAEs..." />
        <div v-else class="services-container">
            <a-card class="mb-4">
                <template #title>
                    {{ zoneId }} - {{ zoneName }}
                </template>
                <template #extra>
                    <a-button type="primary" @click="openInGoogleMaps">
                        Abrir no Google Maps
                    </a-button>
                </template>

                <div v-if="selectedRows.length">
                    <h4 class="mb-2">CNAEs Selecionados</h4>
                    <a-list bordered size="small" class="mb-2">
                        <template v-for="item in selectedRows" :key="item.id">
                            <a-list-item>
                                <div class="list-item-inline">
                                    <div class="item-info">
                                        <strong>{{ item.cnae_id }}</strong> — {{ item.description }}
                                    </div>

                                    <a-checkbox
                                        class="item-checkbox"
                                        :checked="item.isAutoDeclared"
                                        @change="e => onToggleAutoDeclared(item, e.target.checked)"
                                    >
                                        Ponto de Referência
                                    </a-checkbox>

                                    <i
                                        class="fas fa-trash-alt delete-icon"
                                        title="Remover da lista"
                                        @click="removeSelectedItem(item)"
                                    ></i>
                                </div>
                            </a-list-item>
                        </template>
                    </a-list>

                    <a-button
                        type="primary"
                        :disabled="!selectedRows.length"
                        @click="emitDocuments"
                    >
                        Imprimir Comprovante
                    </a-button>
                </div>
            </a-card>

            <a-input-search
                v-model:value="searchTerm"
                placeholder="Buscar por código ou descrição"
                @search="filterCnaes"
                style="width: 300px; margin-bottom: 16px;"
            />

            <a-table
                :data-source="filteredCnaes"
                :columns="columns"
                row-key="id"
                bordered
                :pagination="{ pageSize: 10, showSizeChanger: false }"
                :row-selection="rowSelection"
                @row-click="onRowClick"
            >
                <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'permission'">
                        <i
                            :class="permissionIcon(record.permission)"
                            :title="permissionTooltip(record.permission)"
                        ></i>
                    </template>
                </template>
            </a-table>
        </div>
    </div>
</template>

<script setup>
import { h, ref, watch, onMounted, computed } from 'vue'
import {message, notification} from 'ant-design-vue'
import axios from "axios";
import {LoadingOutlined} from "@ant-design/icons-vue";

const props = defineProps({
    zonaId: Number,
    coordinates: Array,
    propertyId: [Number, null]
})

const zoneId = ref(props.zonaId)
const zoneName = ref('')
const mapCnaeDescription = ref([])
const cnaes = ref([])
const filteredCnaes = ref([])
const searchTerm = ref('')

const loading = ref(true)
const indicator = h(LoadingOutlined, {style: {fontSize: '50px',}, spin: true});

// Estado global da seleção (independente do filtro)
const allSelectedRowKeys = ref(new Set())
const allSelectedRowsMap = ref(new Map())

// Estados que a tabela usa para mostrar seleção
const selectedRowKeys = ref([])
const selectedRows = ref([])

const columns = [
    { title: 'Código', dataIndex: 'cnae_id', key: 'cnae_id' },
    { title: 'Descrição', dataIndex: 'description', key: 'description' },
    { title: 'Permissão', key: 'permission' }
]

function permissionIcon(permission) {
    switch (permission) {
        case 'allowed':
            return 'fas fa-check-circle text-success fa-lg'
        case 'permissible':
            return 'fas fa-exclamation-circle text-warning fa-lg'
        case 'prohibited':
            return 'fas fa-ban text-danger fa-lg'
        default:
            return 'fas fa-question-circle text-muted fa-lg'
    }
}

function permissionTooltip(value) {
    return {
        allowed: 'Permitido',
        permissible: 'Permissível',
        prohibited: 'Proibido'
    }[value] || value
}

const getCnaes = async () => {
    try {
        const { data: classes } = await axios.get('/proxy/ibge/cnae/classes')

        const subclassesRequests = classes.map(cnae => axios.get(`/proxy/ibge/cnae/classes/${cnae.id}/subclasses`))

        const subclassesResponses = await Promise.all(subclassesRequests)

        return subclassesResponses.flatMap(response =>
            response.data.map(subclasse => ({
                value: `${subclasse.id} - ${subclasse.descricao}`,
                description: subclasse.descricao,
                cnaeId: subclasse.id
            }))
        )
    } catch (e) {
        message.error('Erro ao buscar CNAEs')
        return []
    }
}

function filterCnaes() {
    const term = searchTerm.value.toLowerCase()
    filteredCnaes.value = cnaes.value.filter(cnae =>
        cnae.cnae_id.toLowerCase().includes(term) ||
        cnae.description.toLowerCase().includes(term)
    )
}

function onSelectChange(keys, rows) {
    rows.forEach(row => {
        if (!allSelectedRowKeys.value.has(row.id)) {
            if (row.isAutoDeclared === undefined) {
                row.isAutoDeclared = false
            }
            allSelectedRowKeys.value.add(row.id)
            allSelectedRowsMap.value.set(row.id, row)
        } else {
            const existing = allSelectedRowsMap.value.get(row.id)
            if (existing) {
                existing.isAutoDeclared = row.isAutoDeclared ?? false
                allSelectedRowsMap.value.set(row.id, existing)
            }
        }
    })

    const currentPageIds = filteredCnaes.value.map(item => item.id)
    currentPageIds.forEach(id => {
        if (!keys.includes(id)) {
            allSelectedRowKeys.value.delete(id)
            allSelectedRowsMap.value.delete(id)
        }
    })

    selectedRowKeys.value = Array.from(allSelectedRowKeys.value)
    selectedRows.value = Array.from(allSelectedRowsMap.value.values())
}

function onRowClick(record) {
    const keys = Array.from(allSelectedRowKeys.value)
    const rows = Array.from(allSelectedRowsMap.value.values())

    const index = keys.indexOf(record.id)

    if (index > -1) {
        keys.splice(index, 1)
        const rowIndex = rows.findIndex(r => r.id === record.id)
        if (rowIndex > -1) rows.splice(rowIndex, 1)
    } else {
        rows.push({ ...record, isAutoDeclared: false })
        keys.push(record.id)
    }

    onSelectChange(keys, rows)
}

function onToggleAutoDeclared(item, checked) {
    item.isAutoDeclared = checked
    if (allSelectedRowsMap.value.has(item.id)) {
        const existing = allSelectedRowsMap.value.get(item.id)
        existing.isAutoDeclared = checked
        allSelectedRowsMap.value.set(item.id, existing)
        selectedRows.value = Array.from(allSelectedRowsMap.value.values())
    }
}

function removeSelectedItem(item) {
    allSelectedRowKeys.value.delete(item.id)
    allSelectedRowsMap.value.delete(item.id)
    selectedRowKeys.value = Array.from(allSelectedRowKeys.value)
    selectedRows.value = Array.from(allSelectedRowsMap.value.values())
}

const rowSelection = computed(() => ({
    selectedRowKeys: selectedRowKeys.value,
    onChange: onSelectChange,
    hideSelectAll: true
}))

async function emitDocuments() {
    const data = {
        propertyId: props.propertyId,
        coordinates: props.coordinates,
        cnaes: selectedRows.value,
    }

    try {
        const response = await window.axios.post(
            'api/viabilidade/emit-proof-research',
            data,
            { responseType: 'blob' }
        )

        const contentDisposition = response.headers['content-disposition']
        let filename = 'comprovante.pdf'

        if (contentDisposition) {
            const match = contentDisposition.match(/filename="?(.+?)"?$/)
            if (match) {
                filename = decodeURIComponent(match[1])
            }
        }

        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        notification.success({
            message: 'Comprovante gerado',
            description: 'O arquivo foi baixado com sucesso.'
        })

    } catch (err) {
        openNotification('error', err?.response?.data?.message || 'Erro ao emitir documento')
    }
}

function openNotification(type, message) {
    notification[type]({
        message: 'Notificação',
        description: message
    })
}

const fetchData = async () => {
    try {
        const response = (await window.axios.get(`api/viabilidade/zoneamentos/${zoneId.value}`)).data.data

        const permissionMap = new Map()
        response.type_services.forEach(ts => {
            ts.cnaes?.forEach(cn => {
                permissionMap.set(cn.cnae_id, ts.pivot.type_permission)
            })
        })

        const allCnaes = []
        mapCnaeDescription.value = await getCnaes()

        mapCnaeDescription.value.forEach(obj => {
            allCnaes.push({
                id: obj.cnaeId,
                cnae_id: obj.cnaeId,
                description: obj.description,
                permission: permissionMap.get(obj.cnaeId) || 'prohibited'
            })
        })
        cnaes.value = allCnaes
        filteredCnaes.value = allCnaes
        zoneName.value = response.objective
    } catch (err) {
        openNotification('error', err?.response?.data?.message || 'Erro ao carregar os dados')
    } finally {
        loading.value = false
    }
}

function openInGoogleMaps() {
    const url = `https://www.google.com/maps?q=${props.coordinates[1]},${props.coordinates[0]}`
    window.open(url, '_blank')
}

onMounted(fetchData)
watch(searchTerm, filterCnaes)
</script>

<style scoped>
.services-container {
    position: absolute;
    top: 0;
}

.mb-4 {
    margin-bottom: 1rem;
}
.mb-2 {
    margin-bottom: 0.5rem;
}
.fa-lg {
    font-size: 1.5rem;
    cursor: default;
}

.list-item-inline {
    display: flex;
    align-items: center;
    width: 100%;
}

.item-info {
    flex: 1;
    margin-right: 1rem;
    word-break: break-word;
}

.item-checkbox {
    margin-left: auto;
}

.delete-icon {
    color: #ff4d4f;
    cursor: pointer;
    margin-left: 1rem;
    transition: color 0.3s;
}

.delete-icon:hover {
    color: #ff7875;
}
</style>
